/**
 * Gemini Live voices and the protocol constants every Gemini Live model shares.
 */

import { type GeminiLiveThinkingLevel } from "./GeminiLiveTunables.schema";

/**
 * Gemini Live API voices.
 * @see https://ai.google.dev/gemini-api/docs/live-api
 */
export const GEMINI_LIVE_VOICES = [
  "Puck",
  "Charon",
  "Kore",
  "Fenrir",
  "Aoede",
  "Leda",
  "Orus",
  "Zephyr"
] as const;
export type GeminiLiveVoice = (typeof GEMINI_LIVE_VOICES)[number];

/**
 * Gemini Live API protocol configuration.
 *
 * Runtime constants every Gemini Live model shares. Anything that differs by
 * model — id, display name, context window, pricing, which setup fields it
 * accepts — lives in GEMINI_LIVE_MODELS, and a session reads it for the model
 * its deployment selected (`geminiModel`).
 *
 * Session behavior:
 *   - Sliding window compression enabled — no hard session time limit
 *   - Connection lifetime differs by model — `connectionEnd` in GEMINI_LIVE_MODELS
 *   - Resume handle valid for 2 hours after disconnect
 *   - Context quality degrades gradually on very long calls (sliding window tradeoff)
 */
export const GEMINI_LIVE_PROTOCOL = {
  /**
   * WebSocket endpoint (API key appended at runtime) — the one the API reference
   * documents, https://ai.google.dev/api/live. Both Live models answer on it;
   * `14-31-production-setup` and `14-38-production-setup` record each accepting
   * the exact frame the setup builder sends and speaking a greeting.
   */
  wsUrlBase:
    "wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1beta.GenerativeService.BidiGenerateContent",
  /** Max session duration in seconds (audio-only). */
  maxSessionDurationSeconds: 900,
  /** Sampling temperature. */
  temperature: 0.7,
  /** Default thinking level — "medium" balances quality and latency for phone calls. */
  defaultThinkingLevel: "medium" as GeminiLiveThinkingLevel,
  /** Default Gemini voice. */
  defaultVoice: "Kore" as GeminiLiveVoice,
  /** All available Gemini voices. */
  voices: GEMINI_LIVE_VOICES as readonly string[]
} as const;
